import {
  APP_INITIALIZER,
  EnvironmentProviders,
  ModuleWithProviders,
  NgModule,
  makeEnvironmentProviders,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AUTH_CONFIG, NgxAuthConfig, NgxAuthModuleConfig } from './interfaces/auth-config.interface';
import { NgxAuthService } from './services/ngx-auth.service';

/**
 * Factory function for APP_INITIALIZER that ensures auth state is restored
 * before the application starts routing. This prevents guards from redirecting
 * to login when a valid token exists but hasn't been validated yet.
 */
function initializeAuthFactory(authService: NgxAuthService): () => Promise<void> {
  return async () => {
    try {
      await firstValueFrom(authService.initialize());
    } catch {
      // Resolve even on error to allow app to continue
      // The auth service will have set state to null if initialization failed
    }
  };
}

@NgModule({})
export class NgxAuthUtilsModule {
  static forRoot(config: NgxAuthModuleConfig): ModuleWithProviders<NgxAuthUtilsModule> {
    const { authenticationProvider, storageProvider, ...authConfig } = config;

    return {
      ngModule: NgxAuthUtilsModule,
      providers: [
        { provide: AUTH_CONFIG, useValue: authConfig as NgxAuthConfig },
        authenticationProvider,
        storageProvider,
        NgxAuthService,
        {
          provide: APP_INITIALIZER,
          useFactory: initializeAuthFactory,
          deps: [NgxAuthService],
          multi: true,
        },
      ],
    };
  }
}

export function provideNgxAuth(config: NgxAuthModuleConfig): EnvironmentProviders {
  const { authenticationProvider, storageProvider, ...authConfig } = config;

  return makeEnvironmentProviders([
    { provide: AUTH_CONFIG, useValue: authConfig as NgxAuthConfig },
    authenticationProvider,
    storageProvider,
    NgxAuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthFactory,
      deps: [NgxAuthService],
      multi: true,
    },
  ]);
}
